"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
	ArrowRightIcon,
	CheckIcon,
	SparklesIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "@/hooks/useTranslation";
import { useIsMobile } from "@/hooks/useIsMobile";

interface PricingPlan {
	id: "mini" | "standard" | "intensive";
	name: string;
	price: number;
	originalPrice?: number;
	discount?: string;
	description: string;
	features: string[];
	isRecommended?: boolean;
}

interface TrialOffer {
	id: "trial";
	name: string;
	eyebrow: string;
	title: string;
	price: number;
	description: string;
	features: string[];
	cta: string;
}

interface PricingCardsProps {
	contactId: string;
}

export default function PricingCards({ contactId }: PricingCardsProps) {
	const { t } = useTranslation();
	const [isHydrated, setIsHydrated] = useState(false);
	const isMobile = useIsMobile();
	const shouldAnimate = isHydrated && !isMobile;

	useEffect(() => {
		setIsHydrated(true);
	}, []);

	const plans: PricingPlan[] = [
		{
			id: "mini",
			name: t("pricing.mini.name"),
			price: 200,
			description: t("pricing.mini.description"),
			features: [t("pricing.mini.feature1"), t("pricing.mini.feature2")],
		},
		{
			id: "standard",
			name: t("pricing.standard.name"),
			price: 350,
			originalPrice: 400,
			discount: "-13%",
			description: t("pricing.standard.description"),
			features: [
				t("pricing.standard.feature1"),
				t("pricing.standard.feature2"),
			],
			isRecommended: true,
		},
		{
			id: "intensive",
			name: t("pricing.intensive.name"),
			price: 480,
			originalPrice: 600,
			discount: "-20%",
			description: t("pricing.intensive.description"),
			features: [
				t("pricing.intensive.feature1"),
				t("pricing.intensive.feature2"),
			],
		},
	];

	const trialOffer: TrialOffer = {
		id: "trial",
		name: t("pricing.trial.name"),
		eyebrow: t("pricing.trial.eyebrow"),
		title: t("pricing.trial.title"),
		price: 30,
		description: t("pricing.trial.description"),
		features: [t("pricing.trial.feature1"), t("pricing.trial.feature2")],
		cta: t("pricing.trial.cta"),
	};

	const selectClassType = (classType: PricingPlan["id"] | TrialOffer["id"]) => {
		const contactElement = document.getElementById(contactId);

		if (!contactElement) {
			return;
		}

		contactElement.scrollIntoView({ behavior: "smooth" });

		window.setTimeout(() => {
			window.dispatchEvent(
				new CustomEvent("preselectClassType", {
					detail: { classType },
				}),
			);

			window.setTimeout(() => {
				const firstField = contactElement.querySelector(
					"input, select, textarea",
				) as HTMLElement | null;
				firstField?.focus();
			}, 200);
		}, 700);
	};

	return (
		<section className="px-4 py-8 md:py-12">
			<div className="mx-auto max-w-7xl">
				<motion.div
					initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
					whileInView={shouldAnimate ? { opacity: 1, y: 0 } : undefined}
					transition={shouldAnimate ? { duration: 0.6 } : undefined}
					viewport={{ once: true }}
					className="mx-auto mb-6 max-w-3xl text-center md:mb-12"
				>
					<h2 className="mb-3 text-4xl font-bold text-gray-800 md:mb-5 md:text-5xl">
						{t("pricing.title")}
					</h2>
					<p className="text-lg leading-relaxed text-gray-700 md:text-xl">
						{t("pricing.subtitle")}
					</p>
				</motion.div>

				<motion.aside
					initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
					whileInView={shouldAnimate ? { opacity: 1, y: 0 } : undefined}
					transition={shouldAnimate ? { duration: 0.6, delay: 0.12 } : undefined}
					viewport={{ once: true }}
					className="rounded-2xl border border-cyan-200 bg-white/75 p-5 shadow-[0_18px_40px_-30px_rgba(8,145,178,0.5)] backdrop-blur-sm md:p-7"
				>
					<div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_auto] md:items-center md:gap-8 lg:gap-10">
						<div className="min-w-0">
							<div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-700">
								<SparklesIcon className="h-4 w-4" aria-hidden="true" />
								{trialOffer.eyebrow}
							</div>
							<h3 className="text-2xl font-bold text-gray-900">
								{trialOffer.title}
							</h3>
							<p className="mt-2 max-w-3xl text-sm leading-relaxed text-gray-600 md:text-base">
								{trialOffer.description}
							</p>

							<ul className="mt-4 grid gap-2 text-sm text-gray-700 md:inline-grid md:grid-cols-[max-content_minmax(0,auto)] md:gap-x-8 lg:gap-x-10">
								{trialOffer.features.map((feature) => (
									<li key={feature} className="flex gap-2">
										<CheckIcon
											className="mt-0.5 h-4 w-4 flex-shrink-0 text-cyan-700"
											aria-hidden="true"
										/>
										<span>{feature}</span>
									</li>
								))}
							</ul>
						</div>

						<div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center md:justify-end md:gap-7">
							<div className="min-w-[96px] text-left sm:text-right">
								<div className="text-4xl font-bold leading-none text-gray-900">
									{trialOffer.price}€
								</div>
								<div className="mt-1 text-sm font-medium text-gray-500">
									{trialOffer.name}
								</div>
							</div>

							<button
								type="button"
								onClick={() => selectClassType(trialOffer.id)}
								aria-label={`${trialOffer.cta} - ${trialOffer.name}`}
								className="flex w-full items-center justify-center gap-2 rounded-full border border-cyan-300 bg-cyan-50 px-5 py-3 text-sm font-semibold text-cyan-900 transition duration-300 hover:bg-cyan-100 active:translate-y-px sm:w-auto"
							>
								{trialOffer.cta}
								<ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
							</button>
						</div>
					</div>
				</motion.aside>

				<div className="mt-6 grid grid-cols-1 gap-6 md:mt-9 md:grid-cols-3 md:gap-4 lg:grid-cols-[0.94fr_1.08fr_0.94fr] lg:gap-7">
					{plans.map((plan, index) => (
						<motion.article
							key={plan.id}
							initial={shouldAnimate ? { opacity: 0, y: 24 } : false}
							whileInView={
								shouldAnimate ? { opacity: 1, y: plan.isRecommended ? -8 : 0 } : undefined
							}
							transition={
								shouldAnimate ? { duration: 0.55, delay: index * 0.08 } : undefined
							}
							viewport={{ once: true }}
							className={`relative flex min-h-[440px] flex-col rounded-3xl border bg-white p-6 shadow-[0_18px_44px_-34px_rgba(15,23,42,0.28)] transition duration-300 md:grid md:min-h-[640px] md:grid-rows-[132px_132px_1fr_auto] md:p-10 lg:p-11 ${
								plan.isRecommended
									? "border-cyan-300 ring-1 ring-cyan-200 md:shadow-[0_24px_56px_-32px_rgba(8,145,178,0.38)]"
									: "border-gray-200 hover:border-cyan-200"
							}`}
						>
							{plan.isRecommended && (
								<div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-950 px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white shadow-sm">
									{t("pricing.recommended")}
								</div>
							)}

							<div className="md:row-start-1">
								<h3 className="text-2xl font-bold tracking-tight text-gray-950">
									{plan.name}
								</h3>
								<p className="mt-5 min-h-[48px] max-w-[26ch] text-base leading-relaxed text-gray-600">
									{plan.description}
								</p>
							</div>

							<div className="mt-8 md:row-start-2 md:mt-0">
								<div className="text-gray-950">
									<div className="flex items-baseline gap-2">
										<span className="text-5xl font-bold leading-none tracking-tight md:text-6xl">
											{plan.price}€
										</span>
										<span className="text-base font-medium text-gray-500">
											{t("pricing.monthlyLabel")}
										</span>
									</div>
									<div
										className={`mt-3 flex h-6 items-center gap-2 ${
											plan.originalPrice ? "" : "invisible"
										}`}
										aria-hidden={!plan.originalPrice}
									>
										<span className="text-base font-medium text-gray-500 line-through">
											{plan.originalPrice ?? plan.price}€
										</span>
										<span className="rounded-full bg-cyan-50 px-2.5 py-1 text-xs font-medium text-cyan-600">
											{plan.discount ?? "-0%"}
										</span>
									</div>
								</div>
							</div>

							<ul className="mt-8 flex-grow space-y-6 md:row-start-3 md:mt-0">
								{plan.features.map((feature) => (
									<li key={feature} className="flex gap-4 text-base text-gray-700">
										<span
											className={`mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center ${
												plan.id === "mini"
													? "text-cyan-500"
													: plan.isRecommended
														? "rounded-full bg-cyan-50 text-cyan-500"
														: "rounded-full bg-gray-200 text-cyan-500"
											}`}
										>
											<CheckIcon className="h-4 w-4" aria-hidden="true" />
										</span>
										<span className="leading-relaxed">{feature}</span>
									</li>
								))}
							</ul>

							<button
								type="button"
								onClick={() => selectClassType(plan.id)}
								aria-label={`${t("pricing.selectPlan")} - ${plan.name}`}
								className={`mt-9 flex w-full items-center justify-center gap-3 rounded-xl px-5 py-3.5 text-sm font-semibold tracking-[0.08em] transition duration-300 active:translate-y-px md:row-start-4 md:py-4 ${
									plan.isRecommended
										? "bg-gray-950 text-white hover:bg-gray-800"
										: "border border-gray-950 bg-white text-gray-950 hover:bg-gray-950 hover:text-white"
								}`}
							>
								{t("pricing.selectPlan")}
								<ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
							</button>
						</motion.article>
					))}
				</div>

				<motion.div
					initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
					whileInView={shouldAnimate ? { opacity: 1, y: 0 } : undefined}
					transition={shouldAnimate ? { duration: 0.6, delay: 0.35 } : undefined}
					viewport={{ once: true }}
					className="mt-5 text-center md:mt-8"
				>
					<p className="mx-auto max-w-4xl text-xs leading-relaxed text-gray-600 md:text-sm">
						{t("pricing.disclaimer")}
					</p>
				</motion.div>
			</div>
		</section>
	);
}
